import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Chip, Button } from "@mui/material";
import dayjs from "dayjs";

import { Surface, SectionTitle, DataTable, FilterBar, StatusChip } from "components/ui";
import { T, inr, statusTone, tint } from "theme/tokens";
import * as api from "../api";

// Quick ranges, because "last 7 days" is what ops actually ask for.
const PRESETS = [
  { key: "7d", label: "7 days", days: 6 },
  { key: "30d", label: "30 days", days: 29 },
  { key: "90d", label: "90 days", days: 89 },
];

const today = () => dayjs().format("YYYY-MM-DD");
const daysAgo = (n) => dayjs().subtract(n, "day").format("YYYY-MM-DD");

export default function OrdersTab({ pharmacyId }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    deliveryTypes: [],
    paymentStatuses: [],
  });

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Typing in the search box must not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // The dropdown is built from the statuses this pharmacy's orders actually
  // use — `status` has no enum server-side, so a hardcoded list would hide
  // orders whose status nobody remembered to add.
  useEffect(() => {
    api
      .getOrderFilters(pharmacyId)
      .then((res) => setFilterOptions(res.filters))
      .catch(() => {});
  }, [pharmacyId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.getOrders(pharmacyId, {
        page: paginationModel.page + 1, // the API is 1-based, DataGrid is 0-based
        limit: paginationModel.pageSize,
        search: debounced,
        from,
        to,
        status,
        deliveryType,
        paymentStatus,
      });
      setRows(res.orders || []);
      setStatusCounts(res.statusCounts || {});
      setTotal(res.pagination?.total || 0);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not load orders."
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    pharmacyId,
    paginationModel,
    debounced,
    from,
    to,
    status,
    deliveryType,
    paymentStatus,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [debounced, from, to, status, deliveryType, paymentStatus]);

  const applyPreset = (days) => {
    setFrom(daysAgo(days));
    setTo(today());
  };

  const reset = () => {
    setSearch("");
    setFrom("");
    setTo("");
    setStatus("");
    setDeliveryType("");
    setPaymentStatus("");
  };

  const columns = useMemo(
    () => [
      {
        field: "orderId",
        headerName: "Order",
        width: 190,
        renderCell: (params) => (
          <Box sx={{ py: 0.6 }}>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.78rem",
                fontWeight: 600,
                color: T.primary,
              }}
            >
              {params.row.orderId}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: T.subtle }}>
              {params.row.createdAt
                ? dayjs(params.row.createdAt).format("DD MMM YYYY, hh:mm A")
                : "—"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "customerName",
        headerName: "Customer",
        width: 180,
        renderCell: (params) => (
          <Box sx={{ py: 0.6 }}>
            <Typography sx={{ fontSize: "0.83rem", color: T.ink }}>
              {params.row.customerName || "—"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.subtle }}>
              {params.row.customerPhone || ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 170,
        renderCell: (params) => <StatusChip status={params.row.status} />,
      },
      {
        field: "deliveryType",
        headerName: "Fulfilment",
        width: 130,
        renderCell: (params) => (
          <Typography sx={{ fontSize: "0.8rem", color: T.muted, textTransform: "capitalize" }}>
            {params.row.deliveryType || "—"}
          </Typography>
        ),
      },
      {
        field: "payment",
        headerName: "Payment",
        width: 150,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ py: 0.6 }}>
            <StatusChip
              status={params.row.payment?.status}
              label={params.row.payment?.status || "—"}
            />
            <Typography sx={{ fontSize: "0.7rem", color: T.subtle, mt: 0.3 }}>
              {params.row.payment?.method || ""}
            </Typography>
          </Box>
        ),
      },
      {
        field: "totalPrice",
        headerName: "Amount",
        width: 120,
        renderCell: (params) => (
          <Typography
            sx={{ fontSize: "0.85rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
          >
            {inr(params.row.payment?.final_amount ?? params.row.totalPrice)}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/order/individual/${params.row.orderId}`);
            }}
            sx={{ textTransform: "none", fontSize: "0.75rem", color: T.secondary }}
          >
            View
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const chips = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]);

  return (
    <Stack spacing={2}>
      <Surface sx={{ p: 2 }}>
        <SectionTitle
          action={
            <Stack direction="row" spacing={0.6}>
              {PRESETS.map((preset) => (
                <Button
                  key={preset.key}
                  size="small"
                  onClick={() => applyPreset(preset.days)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.72rem",
                    minWidth: 0,
                    px: 1,
                    color: from === daysAgo(preset.days) && to === today() ? T.primary : T.muted,
                    fontWeight: from === daysAgo(preset.days) && to === today() ? 700 : 500,
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>
          }
        >
          Filters
        </SectionTitle>

        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Order ID, customer, phone, AWB…"
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          onReset={reset}
          onRefresh={load}
          selects={[
            {
              key: "status",
              label: "Status",
              value: status,
              onChange: setStatus,
              minWidth: 190,
              options: filterOptions.statuses.map((s) => ({ value: s, label: s })),
            },
            {
              key: "deliveryType",
              label: "Fulfilment",
              value: deliveryType,
              onChange: setDeliveryType,
              minWidth: 150,
              options: filterOptions.deliveryTypes.map((s) => ({ value: s, label: s })),
            },
            {
              key: "paymentStatus",
              label: "Payment",
              value: paymentStatus,
              onChange: setPaymentStatus,
              minWidth: 160,
              options: filterOptions.paymentStatuses.map((s) => ({ value: s, label: s })),
            },
          ]}
        />

        {/* Counts describe the current filter, not the pharmacy's whole
            history, so they stay meaningful as the range narrows. */}
        {chips.length > 0 && (
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {chips.map(([label, count]) => {
              const active = status === label;
              const tone = statusTone(label);
              return (
                <Chip
                  key={label}
                  size="small"
                  clickable
                  onClick={() => setStatus(active ? "" : label)}
                  label={`${label} · ${count}`}
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: active ? "#fff" : tone,
                    bgcolor: active ? tone : tint(tone, 0.12),
                    border: `1px solid ${tint(tone, 0.3)}`,
                  }}
                />
              );
            })}
          </Stack>
        )}
      </Surface>

      <DataTable
        rows={rows}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage="No orders match these filters."
        getRowId={(row) => row._id || row.orderId}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        height={620}
      />
    </Stack>
  );
}
